import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface AnimatedNumberProps {
  /** The target value. Re-animates from the previous displayed value each time this changes. */
  value: number;
  /** Format the interpolated numeric into a string. Receives the in-flight value. */
  format?: (v: number) => string;
  /** Total animation duration in ms. */
  durationMs?: number;
  className?: string;
  /** Optional inline style. */
  style?: React.CSSProperties;
  /** Accessible static label for screen readers (avoids reading the changing number mid-flight). */
  ariaLabel?: string;
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Shared hook: interpolate from a previous numeric value to a new one via RAF.
 * Exposed so `CounterTile`, `AnimatedNumber`, and any future counter-like
 * components stay consistent (ease-out cubic, reduced-motion respect).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAnimatedNumber(target: number, durationMs = 900): number {
  const reduce = prefersReducedMotion();
  const [displayed, setDisplayed] = useState<number>(() => (reduce ? target : 0));
  const fromRef = useRef<number>(reduce ? target : 0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (reduce) {
      setDisplayed(target);
      fromRef.current = target;
      return;
    }
    const from = fromRef.current;
    if (from === target) return;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      const v = from + (target - from) * eased;
      setDisplayed(v);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = target;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs, reduce]);

  return displayed;
}

/**
 * Renders a numeric value that smoothly tweens on every change.
 *
 * Unlike `CounterTile`, this is just the number — no tile chrome. Use it
 * inside `MetricCard`, inline KPI strips, or any place where a single
 * number needs to feel responsive when filters update.
 */
export function AnimatedNumber({
  value,
  format,
  durationMs = 900,
  className,
  style,
  ariaLabel,
}: AnimatedNumberProps) {
  const displayed = useAnimatedNumber(value, durationMs);
  const text = format ? format(displayed) : Math.round(displayed).toLocaleString();
  return (
    <span
      className={cn(className)}
      style={{ fontFeatureSettings: '"tnum"', ...style }}
      aria-label={ariaLabel ?? format?.(value) ?? String(value)}
    >
      {text}
    </span>
  );
}
